import type { StyleSpecification } from 'maplibre-gl';

import { prefersDark } from './theme';

/**
 * Light, dark, satellite and terrain — `PLAN.md` §6 Phase 2's four basemaps.
 *
 * ## Why none of these needs a key, and why that stopped being free by default
 *
 * Phase 2 originally drew light and dark from CARTO's raster basemaps, which
 * had been keyless for years. In late August 2026 CARTO started stamping
 * anonymous tiles with a large "API KEY REQUIRED" watermark: the tiles still
 * load, they are just unusable. A free key is a one-minute signup, but a key
 * is a key — it has to be provisioned, stored and rotated, and this app has no
 * server to keep one on and no account to tie one to. So the requirement here
 * is stricter than "free": **no key, no signup, no account, no query
 * parameter**, for every basemap, forever.
 *
 * Three sources meet that bar and are used below:
 *
 *  - **OpenFreeMap** (`tiles.openfreemap.org`) — OpenStreetMap vector tiles,
 *    explicitly free with no API key, no registration and no request limit.
 *    This is the vector basemap `ARCHITECTURE.md` and `PLAN.md` §3 wanted all
 *    along, and it also carries its own glyphs and sprites, so nothing here
 *    reaches for a second host to render labels.
 *  - **Esri World Imagery** (`server.arcgisonline.com`) — keyless satellite
 *    imagery, unchanged from the first cut of this file.
 *  - **AWS Terrain Tiles** (`s3.amazonaws.com/elevation-tiles-prod`) — public
 *    Terrarium-encoded elevation, on AWS's Open Data programme.
 *
 * Never `tile.openstreetmap.org`, whose usage policy `PLAN.md` §3 rules out
 * for anything but the OSM Foundation's own map page.
 *
 * ## Why every style has a fallback
 *
 * The old version of this file avoided vector styles on the grounds that "a
 * wrong style URL fails blank and silent". That is a real failure mode, and
 * the CARTO change is proof that a third party can break a basemap without
 * warning. So a style that is fetched by URL now names a `fallback` — a
 * self-contained raster style, on a different host, that `FlightMap` swaps in
 * if the URL does not load. A basemap can go away; the map cannot.
 */

/**
 * How long a basemap fetched by URL gets before its fallback takes over.
 *
 * Long enough that a slow connection is not mistaken for a dead host, short
 * enough that nobody sits in front of an empty rectangle (or, for the export
 * composer, exports one) wondering. A style that is already an object never
 * starts this clock — there is nothing to fetch and nothing to fall back from.
 */
export const STYLE_LOAD_TIMEOUT_MS = 8000;

export type BasemapId = 'light' | 'dark' | 'satellite' | 'terrain';

export interface Basemap {
  id: BasemapId;
  label: string;
  /** A style URL (fetched by MapLibre) or an inline style object. */
  style: string | StyleSpecification;
  /** Applied if `style` is a URL that fails to load. Always self-contained. */
  fallback?: StyleSpecification;
  /**
   * Shown instead of `attribution` while the fallback is in use. `FlightMap`
   * renders the credit itself rather than letting MapLibre's control do it, so
   * it has to be told when the credit changes — a fallback on another host is
   * another host's data, and crediting the wrong one is worse than either.
   */
  fallbackAttribution?: string;
  attribution: string;
}

const OPENFREEMAP_ATTRIBUTION = '© OpenFreeMap © OpenMapTiles © OpenStreetMap contributors';
const ESRI_ATTRIBUTION = 'Esri, Maxar, Earthstar Geographics, and the GIS User Community';
const TERRAIN_ATTRIBUTION = 'AWS Terrain Tiles, Mapzen, OpenStreetMap contributors';

/**
 * One of Esri's keyless raster basemaps, as a complete style.
 *
 * The `Base` layers carry no place names — Esri splits labels into a separate
 * `Reference` service — so both are stacked, which is how Esri's own viewers
 * use them.
 */
function esriCanvasStyle(variant: 'Light_Gray' | 'Dark_Gray'): StyleSpecification {
  const service = (kind: 'Base' | 'Reference') =>
    `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_${variant}_${kind}/MapServer/tile/{z}/{y}/{x}`;

  return {
    version: 8,
    sources: {
      'canvas-base': {
        type: 'raster',
        tiles: [service('Base')],
        tileSize: 256,
        maxzoom: 16,
        attribution: ESRI_ATTRIBUTION,
      },
      'canvas-labels': {
        type: 'raster',
        tiles: [service('Reference')],
        tileSize: 256,
        maxzoom: 16,
        attribution: ESRI_ATTRIBUTION,
      },
    },
    layers: [
      { id: 'canvas-base', type: 'raster', source: 'canvas-base' },
      { id: 'canvas-labels', type: 'raster', source: 'canvas-labels' },
    ],
  };
}

function satelliteStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      imagery: {
        type: 'raster',
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: ESRI_ATTRIBUTION,
      },
    },
    layers: [{ id: 'imagery', type: 'raster', source: 'imagery' }],
  };
}

/**
 * Shaded relief from a public elevation-tile source, no roads or labels.
 * `hillshade` needs a `raster-dem` source, so this is the one basemap that is
 * not a plain image tile — everything else about it is as keyless as the rest.
 */
function terrainStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      'terrain-dem': {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 15,
        encoding: 'terrarium',
        attribution: TERRAIN_ATTRIBUTION,
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#e7e2d3' } },
      {
        id: 'hillshade',
        type: 'hillshade',
        source: 'terrain-dem',
        paint: { 'hillshade-exaggeration': 0.7 },
      },
    ],
  };
}

const LIGHT: Basemap = {
  id: 'light',
  label: 'Light',
  // Positron: OpenFreeMap's quiet style — POIs dropped, road labels held back
  // to higher zooms — which is exactly what a basemap under a flight track
  // should be.
  style: 'https://tiles.openfreemap.org/styles/positron',
  fallback: esriCanvasStyle('Light_Gray'),
  attribution: OPENFREEMAP_ATTRIBUTION,
  fallbackAttribution: ESRI_ATTRIBUTION,
};

export const BASEMAPS: readonly Basemap[] = [
  LIGHT,
  {
    id: 'dark',
    label: 'Dark',
    style: 'https://tiles.openfreemap.org/styles/dark',
    fallback: esriCanvasStyle('Dark_Gray'),
    attribution: OPENFREEMAP_ATTRIBUTION,
    fallbackAttribution: ESRI_ATTRIBUTION,
  },
  {
    id: 'satellite',
    label: 'Satellite',
    style: satelliteStyle(),
    attribution: ESRI_ATTRIBUTION,
  },
  {
    id: 'terrain',
    label: 'Terrain',
    style: terrainStyle(),
    attribution: TERRAIN_ATTRIBUTION,
  },
];

export const DEFAULT_BASEMAP_ID: BasemapId = 'light';

export function getBasemap(id: BasemapId): Basemap {
  return BASEMAPS.find((basemap) => basemap.id === id) ?? LIGHT;
}

/**
 * The basemap to open with: dark chrome deserves a dark map.
 *
 * `theme.ts` reads the same two signals `globals.css` does, so the map never
 * opens as a white rectangle in a dark room. It is a starting value only — the
 * picker still does what it says afterwards.
 */
export function preferredBasemapId(doc: Document = document): BasemapId {
  return prefersDark(doc) ? 'dark' : DEFAULT_BASEMAP_ID;
}
